import { useState, useRef, useCallback, useEffect } from 'react';
import { saveRecording } from '../../../services/recordings.service.ts';
import {
  createMeetingRecordingStream,
  type MeetingRecordingStream,
} from '../utils/meetingRecordingStream.ts';

interface UseMeetingRecordingOptions {
  meetingId: string;
  meetingTitle?: string;
  containerRef: React.RefObject<HTMLElement | null>;
  audioStream?: MediaStream | null;
}

function getSupportedMimeType(): string {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) ?? 'video/webm';
}

export function useMeetingRecording({
  meetingId,
  meetingTitle,
  containerRef,
  audioStream,
}: UseMeetingRecordingOptions) {
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const captureStreamRef = useRef<MeetingRecordingStream | null>(null);
  const mimeTypeRef = useRef('video/webm');

  const stopCapture = useCallback(() => {
    captureStreamRef.current?.stopCapture();
    captureStreamRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
      recorderRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (isRecording || isSaving) return;

    const container = containerRef.current;
    if (!container) {
      alert('Kayıt başlatılamadı.');
      return;
    }

    if (typeof MediaRecorder === 'undefined') {
      alert('Tarayıcınız video kaydını desteklemiyor.');
      return;
    }

    try {
      const extraAudio = audioStream ? [audioStream] : [];
      const captureStream = await createMeetingRecordingStream(container, extraAudio);
      captureStreamRef.current = captureStream;

      const mimeType = getSupportedMimeType();
      mimeTypeRef.current = mimeType;

      const recorder = new MediaRecorder(captureStream, { mimeType });
      chunksRef.current = [];
      startedAtRef.current = Date.now();

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = async () => {
        setIsSaving(true);
        try {
          const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
          if (blob.size === 0) {
            alert('Kayıt boş. Kameranızın açık olduğundan emin olun.');
            return;
          }
          const durationSeconds = Math.max(
            1,
            Math.round((Date.now() - startedAtRef.current) / 1000)
          );
          const dateLabel = new Date().toLocaleString('tr-TR');
          await saveRecording({
            id: `${meetingId}_${Date.now()}`,
            meetingId,
            title: meetingTitle
              ? `${meetingTitle} — ${dateLabel}`
              : `Toplantı ${meetingId} — ${dateLabel}`,
            createdAt: Date.now(),
            durationSeconds,
            sizeBytes: blob.size,
            blob,
          });
          alert('Kayıt tamamlandı. Panel → Kayıtlar bölümünden izleyebilirsiniz.');
        } catch (error) {
          console.error('Kayıt kaydedilemedi:', error);
          alert('Kayıt kaydedilemedi.');
        } finally {
          chunksRef.current = [];
          stopCapture();
          setIsSaving(false);
          setIsRecording(false);
        }
      };

      recorder.start(1000);
      recorderRef.current = recorder;
      setIsRecording(true);
    } catch (error) {
      console.error('Kayıt başlatma hatası:', error);
      stopCapture();
      if ((error as Error).message === 'NO_VIDEO') {
        alert('Kayıt için görüntü bulunamadı. Kameranızın açık olduğundan emin olun.');
      } else {
        alert('Kayıt başlatılamadı. Lütfen tekrar deneyin.');
      }
    }
  }, [
    audioStream,
    containerRef,
    isRecording,
    isSaving,
    meetingId,
    meetingTitle,
    stopCapture,
  ]);

  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      void startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  useEffect(() => {
    return () => {
      if (recorderRef.current?.state === 'recording') {
        recorderRef.current.stop();
        recorderRef.current = null;
      }
      stopCapture();
    };
  }, [stopCapture]);

  return {
    isRecording,
    isSaving,
    startRecording,
    stopRecording,
    toggleRecording,
  };
}
